import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { enumPaymentBillType, enumPaymentProvider } from '@data-layer/enums/payments';
import { UtilsHelper } from '@helpers/utils-helper';
import { catchError, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { BillsRepository, BluesnapRepository, EntitiesRepository, LCLBillsRepository } from '@data-layer/repositories';
import { MarineInsuranceFacade } from '@shared/marine-insurance-shared/services';
import { AlertService } from '@services/alert.service';
import { BillItemModel, BillModel } from '@data-layer/models/payments';
import { environment } from '@env/environment';
import {
  ALIPAY_BLUESNAP_CONST,
  BANKTRANSFER_BLUESNAP_CONST,
  BLUESNAP_PAYMENT_FLAG,
  CC_BLUESNAP_CONST,
  CN_BLUESNAP_CONST,
  CNY_BLUESNAP_CONST,
  ECP_BLUESNAP_CONST,
  PAYSAFECARD_BLUESNAP_CONST,
  REALTIMEBANK_BLUESNAP_CONST,
  US_BLUESNAP_CONST,
  USD_BLUESNAP_CONST,
  WIRE_BLUESNAP_CONST,
} from '../const';
import { filterFeeBillItems } from '@app/shared/helpers';
import { CompanyModel } from '@data-layer/models/companies';
import { enumEnvironmentName } from '@env/enumEnvironmentName';
import { UserModel } from '@data-layer/models/user';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { enumPaymentEntity } from '@app/modules/payment/enums';
import { enumCustomEventLogName, enumCustomEventLogState } from '@app/data-layer/enums/common';
import { ConsoleLogService } from '@services/console-log.service';
import { LoggerService } from '@services/logger.service';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(
    private loggerService: LoggerService,
    private consoleLogService: ConsoleLogService,
    private billsRepository: BillsRepository,
    private LclBillsRepository: LCLBillsRepository,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private bluesnapRepository: BluesnapRepository,
    private alertService: AlertService,
    private entitiesRepository: EntitiesRepository,
  ) {}

  public handleIframeUrl(
    paymentBillType: enumPaymentBillType,
    paymentProvider: enumPaymentProvider,
    referenceNumbers: string[],
    company: CompanyModel,
    user: UserModel,
    paymentCodeId: number,
    paymentBluesnapTypeName: string,
    coupon: string,
    redirectDomain: string = '/',
    returnUrl: string = '/',
    billToEntity: EntityModel,
    paymentMethodId?: number,
  ): Observable<string> {
    const redirectSuffix: string = `invoiceId=!{invoice.id}&invoiceStatus=!{invoice.status}&bsOrderId=!{bs.order.id}&merchantTransactionId=!{merchant.transaction.id}&method=!{selected.payment.method}`;
    const formattedReferenceNumbers: string = referenceNumbers
      .map((referenceNumber: string) => `referenceNumbers=${referenceNumber}`)
      .join('&');
    const redirectUrlUpdated: string = encodeURIComponent(
      `${redirectDomain}/payment/result?${formattedReferenceNumbers}&returnUrl=${returnUrl}&${BLUESNAP_PAYMENT_FLAG}=true&${redirectSuffix}`,
    );

    return this.handleBill(
      paymentBillType,
      paymentProvider,
      referenceNumbers,
      company.id,
      paymentCodeId,
      billToEntity,
      coupon,
      paymentMethodId,
    ).pipe(
      switchMap((bills: BillModel[]) => {
        bills = (bills || []).filter((item: BillModel) => item.providerType === enumCompanyPaymentType.INSTANT);

        const amount: number = bills.reduce((acc: number, curr: BillModel) => {
          const currAmount: number = (curr?.items || []).reduce(
            (accItems: number, currItems: BillItemModel) => accItems + currItems.quantity * currItems.price,
            0,
          );
          return acc + currAmount;
        }, 0);

        return combineLatest([
          of(bills),
          this.bluesnapRepository.encryptParams({
            amount: amount.toString(),
            merchantid: environment.bluesnapMerchantId,
            'thankyou.backtosellerurl': redirectUrlUpdated,
          }),
        ]);
      }),
      switchMap(([currentBills, encryptedStr]: [BillModel[], string]) => {
        let paymentValueParams: string = '';
        let index: number = 0;
        const merchantTransactionId: number = currentBills?.[0]?.id;
        let additionalParams: string = '';
        filterFeeBillItems(currentBills);
        (currentBills || [])
          .filter((item: BillModel) => item.providerType === enumCompanyPaymentType.INSTANT)
          .forEach((billItem: BillModel) => {
            (billItem?.items || []).forEach((item: BillItemModel) => {
              paymentValueParams += `&name${index + 1}=${item.referenceNumber} - ${item.description} ${
                item.quantity > 1 ? `(x${item.quantity})` : ''
              }&value${index + 1}=$${item.quantity * item.price}`;
              index++;
            });
          });
        const bluesnapPaymentMethods: string[] = [
          ALIPAY_BLUESNAP_CONST,
          BANKTRANSFER_BLUESNAP_CONST,
          CC_BLUESNAP_CONST,
          ECP_BLUESNAP_CONST,
          PAYSAFECARD_BLUESNAP_CONST,
          REALTIMEBANK_BLUESNAP_CONST,
          WIRE_BLUESNAP_CONST,
        ];
        const bluesnapPaymentMethodStr: string = bluesnapPaymentMethods
          .map((item: string) => {
            return '&payment' + (item.toUpperCase() === paymentBluesnapTypeName.toUpperCase() ? `${item}=Y` : `${item}=N`);
          })
          .join('');
        let country: string =
          billToEntity.address?.country?.code === USA_COUNTRY_CODE ? US_BLUESNAP_CONST : billToEntity.address?.country?.code;
        let currency: string = USD_BLUESNAP_CONST;
        if (paymentBluesnapTypeName.toLowerCase() === ALIPAY_BLUESNAP_CONST) {
          country = CN_BLUESNAP_CONST;
          currency = CNY_BLUESNAP_CONST;
          additionalParams = '&currencydisable=Y&countrydisable=Y';
        }

        if (paymentBluesnapTypeName.toLowerCase() === ECP_BLUESNAP_CONST) {
          additionalParams = '&currencydisable=Y&countrydisable=Y&billingcountryvisible=N';
        }

        const address: string = [
          [billToEntity?.address?.line1, billToEntity?.address?.line2, billToEntity?.address?.line3].join(' '),
          billToEntity?.address?.city,
          [billToEntity?.address?.state?.code, billToEntity?.address?.postalCode].join(' '),
          billToEntity?.address?.country?.description,
        ]
          .filter((item: string) => !!item?.trim())
          .join(', ');

        const url: string = enumEnvironmentName.PROD === environment.name ? 'https://bluesnap.com/' : 'https://sandbox.bluesnap.com/';

        return of(
          `${url}buynow/checkout?enc=${encryptedStr}` +
            `&merchantid=${environment.bluesnapMerchantId}` +
            `&merchanttransactionid=${merchantTransactionId}` +
            `${paymentValueParams}` +
            `&couponboxvisible=N` +
            `&sealmcafeevisible=N` +
            `&sealverisignvisible=N` +
            `&sealvisible=N` +
            `&language=ENGLISH` +
            `&languagevisible=N` +
            `&currency=${currency}` +
            `&storecardvisible=N` +
            bluesnapPaymentMethodStr +
            `&selectedPaymentMethod=${paymentBluesnapTypeName.toUpperCase()}` +
            `&statevisible=N` +
            `&fullname=${user.firstName} ${user.lastName}` +
            `&email=${encodeURIComponent(billToEntity.email || user.email)}` +
            `&zip=${billToEntity.address?.postalCode || ''}` +
            `&address1=${address}` +
            `&city=${billToEntity.address?.city}` +
            `&state=${billToEntity.address?.state?.description}` +
            `&country=${country}` +
            `${additionalParams}`,
        );
      }),
    );
  }

  public createEntity(companyId: number, billToEntity: EntityModel): Observable<EntityModel> {
    return this.entitiesRepository.createEntity(companyId, { ...billToEntity, entityCode: enumEntityModelCode.BILL_TO });
  }

  public handleBill(
    paymentBillType: enumPaymentBillType,
    paymentProvider: enumPaymentProvider,
    referenceNumbers: string[] | Observable<string[]>,
    companyId: number,
    paymentCodeId: number,
    billToEntity: EntityModel,
    coupon: string,
    paymentMethodId?: number,
  ): Observable<BillModel[]> {
    return (!!billToEntity ? this.createEntity(companyId, billToEntity) : of(null)).pipe(
      switchMap((billToEntityCreated: EntityModel) => {
        const billToId: number = billToEntityCreated?.id;
        referenceNumbers = referenceNumbers?.[0] !== enumPaymentEntity.COMPANY ? referenceNumbers : [];

        if (UtilsHelper.isString(referenceNumbers?.[0]) || UtilsHelper.isEmptyArray(referenceNumbers)) {
          switch (paymentBillType) {
            default:
            case enumPaymentBillType.CUSTOM:
              return this.billsRepository
                .create(companyId, {
                  referenceNumbers: referenceNumbers as string[],
                  coupon: coupon,
                  paymentProvider,
                  paymentCodeId,
                  paymentMethodId,
                  billToId,
                })
                .pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));

            case enumPaymentBillType.LCL:
              return this.LclBillsRepository.create(
                companyId,
                referenceNumbers as string[],
                paymentProvider,
                paymentCodeId,
                billToId,
                coupon,
                paymentMethodId,
              ).pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));

            case enumPaymentBillType.MARINE_INSURANCE:
              return this.marineInsuranceFacade
                .createBill(
                  companyId,
                  referenceNumbers as string[],
                  paymentProvider,
                  paymentCodeId,
                  billToId,
                  coupon,
                  undefined,
                  paymentMethodId,
                )
                .pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));
          }
        } else {
          return (referenceNumbers as Observable<string[]>).pipe(
            switchMap((_referenceNumbers: string[]) => {
              switch (paymentBillType) {
                default:
                case enumPaymentBillType.CUSTOM:
                  return this.billsRepository
                    .create(companyId, {
                      referenceNumbers: _referenceNumbers,
                      paymentProvider,
                      paymentCodeId,
                      paymentMethodId,
                      coupon,
                      billToId,
                    })
                    .pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));

                case enumPaymentBillType.LCL:
                  return this.LclBillsRepository.create(
                    companyId,
                    _referenceNumbers,
                    paymentProvider,
                    paymentCodeId,
                    billToId,
                    coupon,
                    paymentMethodId,
                  ).pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));

                case enumPaymentBillType.MARINE_INSURANCE:
                  return this.marineInsuranceFacade
                    .createBill(companyId, _referenceNumbers, paymentProvider, paymentCodeId, billToId, coupon, undefined, paymentMethodId)
                    .pipe(catchError((error: HttpErrorResponse) => this.handleBillError(error, companyId)));
              }
            }),
          );
        }
      }),
    );
  }

  public markForBluesnapPayment(billId: number, state: string, transactionId: string): Observable<{}> {
    return this.bluesnapRepository.paymentStatusNotification(billId, state, transactionId);
  }

  private handleBillError(error: HttpErrorResponse, companyId: number): Observable<never> {
    const errorMessage: string = this.billsRepository.getErrorMessage(error);
    this.alertService.error(errorMessage);
    this.logError(error, companyId);
    return throwError(error);
  }

  private logError(error: HttpErrorResponse, companyId: number): void {
    const consoleErrors: any[] = this.consoleLogService.getLogs('error');
    this.loggerService
      .logCustomEvent({
        eventName: enumCustomEventLogName.PAYMENT_SERVICE_ERROR,
        state: enumCustomEventLogState.FAILED,
        data: {
          apiError: error,
          consoleErrors,
          customData: {
            companyId,
          },
        },
      })
      .pipe(take(1))
      .subscribe();
  }
}
